import { getUserMailboxInfo } from 'owa-client-ids';
import { errorThatWillCauseAlertAndThrow } from 'owa-trace';
import { getInMemorySourceIdForMailboxInfo } from './inMemorySourceList';
import type { CreateAccountResult, RemoveAccountResult } from './types';

/**
 * Defines the interface that the hosting application must implement
 */
export interface PluggableHostAccountSource {
    preSelectAccount?: (sourceId: string) => Promise<void>;
    addM365Account?: (email: string) => Promise<CreateAccountResult>;
    addOutlookDotComAccount?: (email: string) => Promise<CreateAccountResult>;
    removeAccount?: (sourceId: string) => Promise<RemoveAccountResult>;
    getSourceIdForUserIdentity?: (userIdentity?: string | null) => string;
}

// The pluggable host instance
let thePluggableHost: PluggableHostAccountSource = {};

/**
 * Sets the pluggable host
 * @param host provides the host specific implementation for getting account information
 */
export function updatePluggableHost(host?: Partial<PluggableHostAccountSource>) {
    thePluggableHost = { ...thePluggableHost, ...host };
}

/**
 * Restores the pluggable host to the default pluggable host
 */
export function testHookToResetPluggableHost() {
    thePluggableHost = {};
}

export async function preSelectAccount(sourceId: string): Promise<void> {
    if (thePluggableHost.preSelectAccount) {
        return thePluggableHost.preSelectAccount(sourceId);
    }
}

export async function addM365Account(email: string): Promise<CreateAccountResult> {
    if (!thePluggableHost.addM365Account) {
        errorThatWillCauseAlertAndThrow('NoPluggableMethodAddM365Account');
    }

    return thePluggableHost.addM365Account!(email);
}

export async function addOutlookDotComAccount(email: string): Promise<CreateAccountResult> {
    if (!thePluggableHost.addOutlookDotComAccount) {
        errorThatWillCauseAlertAndThrow('NoPluggableMethodAddOutlookDotComAccount');
    }

    return thePluggableHost.addOutlookDotComAccount!(email);
}

export async function removeAccount(sourceId: string): Promise<RemoveAccountResult> {
    if (!thePluggableHost.removeAccount) {
        errorThatWillCauseAlertAndThrow('NoPluggableMethodRemoveAccount');
    }

    return thePluggableHost.removeAccount!(sourceId);
}

/**
 * REFLOW TECHNICAL DEBT
 *
 * We needed a mechanism to go from the UserIdentity to the sourceId, this needs to be supported
 * in both Monarch (meaning native host with multiple account enabled) and in OWA. This method
 * provides support for getting the sourceId based on the user identity.
 *
 * @param userIdentity identifies the mailbox, when not specified the selected mailbox is assumed
 * @returns SourceId for mailbox associated with the supplied user identity
 */
export function getSourceIdForUserIdentity(userIdentity?: string | null): string {
    if (thePluggableHost.getSourceIdForUserIdentity) {
        return thePluggableHost.getSourceIdForUserIdentity(userIdentity);
    }

    // When not set will fall back to the in memory source list
    return getInMemorySourceIdForMailboxInfo(getUserMailboxInfo(userIdentity, true));
}
